import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pagination } from '@/features/common/Pagination';
import { dateRangeFilter } from '@/i18n/dictionaries/dateRangeFilter';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { languageFallbackOf, servedLanguage } from '@/features/publicsite/contentLanguage';
import { usePageLanguageFallback } from '@/features/publicsite/usePageLanguageFallback';
import {
  calendarCountMessage,
  emptyCalendarMessage,
} from '@/features/publicsite/events/calendarMessages';
import { eventFilters, withEventFilters } from '@/features/publicsite/events/eventFilters';
import { EventCard } from '@/features/publicsite/events/EventCard';
import { EventFilterPanel } from '@/features/publicsite/events/EventFilterPanel';
import {
  calendarPage,
  eventsPageHref,
  outOfRangePage,
} from '@/features/publicsite/events/eventPaging';
import { useEventCalendarPage } from '@/features/publicsite/events/useEventCalendarPage';
import { useEventFilterOptions } from '@/features/publicsite/events/useEventFilterOptions';
import type { FilterNavigation } from '@/features/publicsite/types/FilterNavigation';

/**
 * /evenimente — the national calendar, in one interface (tor.md §6.11): every event the filters
 * leave standing, month by month, a page at a time.
 *
 * It used to be two: "Evenimente viitoare" over "Evenimente încheiate", split in the browser out of
 * one unpaged response. The caiet asks for one calendar, and the split cost more than the heading
 * it bought — the upcoming half was empty most of the time, which read as a calendar with nothing
 * in it, while the archive underneath was the whole of what the site had. Which end the calendar
 * opens on is now the order it asks the server for (calendarOrder), and an event that has already
 * happened carries „Încheiat" on its own card rather than being filed under a heading.
 *
 * The page opens on the title and one row: the search, the period, and "Mai multe filtre" after
 * them. Then the events themselves, a flat list of cards in the order asked for — no list heading,
 * no month headings, nothing counted on screen. Everything between the title and the first card
 * was something to read before reaching what the page is for; the months in particular split one
 * short page into pieces and then labelled each piece.
 *
 * The count is still spoken, in the live region below, for a reader who cannot see that the list
 * under the filter has changed. The list is a real list — <ul> of <li> with a name of its own —
 * so that reader is told how many cards there are and can move by item, which a heading over a
 * row of links would not have done.
 *
 * The filters live in the URL, as /stiri's do, and so does the page: a narrowed calendar and the
 * page being read can both be shared, Back undoes either, and a crawler can walk the whole archive.
 */
export function EventsPage() {
  const { t, language } = useTranslation(publicEvents);
  // The period control's own words, so the offer to empty it out of the empty state and the button
  // inside the panel cannot come to say two different things.
  const { t: tPeriod } = useTranslation(dateRangeFilter);
  usePublicPageTitle(t('heading'));
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { regions, themeOptions, organizerOptions } = useEventFilterOptions();
  const page = calendarPage(params);
  const { data, fresh, loading, failed } = useEventCalendarPage(params, page);
  const events = data?.items ?? [];
  usePageLanguageFallback(languageFallbackOf(events.map(servedLanguage), language));

  const filters = eventFilters(params);
  const emptyMessage = emptyCalendarMessage(filters);

  // preventScrollReset, as on /stiri: every filter change is a navigation, and the shell's scroll
  // restoration would otherwise send each one to the top, away from the filter just used. A search
  // refined after a pause replaces its own history entry (listingSearch). withEventFilters drops
  // `page` along the way, so a narrowed calendar starts at its own first page rather than at page
  // four of the calendar it replaced.
  const setFilter = (
    changes: Parameters<typeof withEventFilters>[1],
    navigation?: FilterNavigation,
  ) => setParams(withEventFilters(params, changes), { preventScrollReset: true, ...navigation });

  // A link to page 4 outlives the events that made four pages. Rather than the empty page the
  // endpoint truthfully answers with, the reader lands on the last page there is — replacing the
  // history entry, so Back leaves the calendar instead of bouncing off the page that is gone.
  const lastPage = data ? outOfRangePage(page, data.totalPages) : null;
  useEffect(() => {
    if (lastPage !== null) navigate(eventsPageHref(params, lastPage), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the filters are read as they stand when a page turns out to be past the end; adding `params` would re-run this on every URL rewrite
  }, [lastPage]);

  return (
    <section className="container section">
      <h1>{t('heading')}</h1>

      {/* Spoken, not shown. Polite, so a screen reader hears the new number once a filter has
          changed it without being interrupted mid-sentence, and it waits for the answer to the
          filters now in the URL (`fresh`): the previous count announced over the new list would
          be worse than a moment with no count at all. */}
      <p className="visually-hidden" aria-live="polite">
        {fresh && data ? t(calendarCountMessage(data.totalItems), { count: data.totalItems }) : ''}
      </p>

      <EventFilterPanel
        params={params}
        regions={regions}
        themeOptions={themeOptions}
        organizerOptions={organizerOptions}
        // The card's own results line stays empty: this page announces the count in the live
        // region above, and two live regions saying the same number talk over each other.
        results=""
        onFilter={setFilter}
        onReset={() => setParams(new URLSearchParams(), { preventScrollReset: true })}
      />

      <section className="event-calendar-list">
        {/* The cards on screen belong to the previous request until the current one lands, as on
            /stiri: blanking them on every page turn would bounce the layout under the reader. */}
        {events.length > 0 && (
          <ul className="card-grid" aria-label={t('listAria')} aria-busy={fresh ? undefined : true}>
            {events.map((event) => (
              <li key={event.id}>
                <EventCard event={event} />
              </li>
            ))}
          </ul>
        )}

        {/* A failed request said nothing at all before, leaving the page on its heading; an empty
            list would say the calendar is empty, which is a different sentence and a false one. */}
        {failed && <p className="empty-state">{t('loadFailed')}</p>}
        {!failed && !data && loading && <p className="empty-state">{t('loading')}</p>}
        {!failed && fresh && data && events.length === 0 && (
          <p className="empty-state">
            {t(emptyMessage)}{' '}
            {emptyMessage === 'periodEmptyState' && (
              <button
                type="button"
                className="btn btn--text-primary"
                onClick={() => setFilter({ from: null, to: null })}
              >
                {tPeriod('clear')}
              </button>
            )}
          </p>
        )}
      </section>

      {/* Pagination counts from 0 and this page's URL from 1 (eventPaging), so the two are
          converted at exactly this boundary and nowhere else. */}
      <Pagination
        page={page - 1}
        totalPages={data?.totalPages ?? 0}
        hrefFor={(target) => eventsPageHref(params, target + 1)}
        labels={{
          region: t('paginationAria'),
          previous: t('previous'),
          next: t('next'),
          page: (n) => t('pageAria', { page: n }),
          current: (n) => t('currentPageAria', { page: n }),
        }}
      />
    </section>
  );
}
